// Gates when geolocation watching starts (R-ONB-10 / R-SAFE-01: ask for
// nothing at the start; updates must start from an explicit user tap). This
// holds no DOM and no BrowserGeolocation reference of its own: it is handed
// a small port to start/stop watching, so "no geolocation before tap" is a
// plain unit test with no navigator mocking.

export interface NavigationFix {
  lat: number;
  lng: number;
  accuracyM: number;
}

export interface LocationWatchPort {
  start(onFix: (fix: NavigationFix) => void, onUnavailable?: () => void): void;
  stop(): void;
}

export class NavigationLocationController {
  private shared = false;
  private watching = false;

  constructor(private readonly port: LocationWatchPort) {}

  /** True once location has started (a granted permission or a tap). Never resets on stop(). */
  get isShared(): boolean {
    return this.shared;
  }

  get isWatching(): boolean {
    return this.watching;
  }

  /**
   * Starts the location watch: from the player's tap, the automatic start
   * after a granted permission, or a remount after stop(). A call while
   * already watching is a no-op (does not restart the watch or call the
   * port again).
   */
  share(onFix: (fix: NavigationFix) => void, onUnavailable?: () => void): void {
    if (this.watching) return;
    this.shared = true;
    this.watching = true;
    this.port.start(onFix, () => {
      if (!this.watching) return;
      this.watching = false;
      this.shared = false;
      this.port.stop();
      onUnavailable?.();
    });
  }

  /**
   * Stops watching (navigation cleared or session ended). Leaves `isShared`
   * true: re-entering navigation later does not ask the player to tap again
   * in the same session, only the watch itself is torn down.
   */
  stop(): void {
    if (!this.watching) return;
    this.watching = false;
    this.port.stop();
  }
}
